import { HttpStatusCode } from '@angular/common/http'
import { computed, inject } from '@angular/core'

import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals'

import { Observable, firstValueFrom } from 'rxjs'

import { MakerFilesApi } from '@flowx/designer/modules/ai-builder/services/maker-files.api'
import {
  ConversationFileRemoval,
  type ConversationFileRemovalState,
  type MakerFile,
  MakerFileState,
} from '@flowx/designer/modules/ai-builder/types/conversation-file.types'
import {
  FILE_PANEL_TEXTS,
  type SentFileStanding,
  conversationFiles,
} from '@flowx/designer/modules/ai-builder/utils/conversation-files.util'
import {
  harnessErrorHttpStatus,
  harnessErrorMessage,
  inServiceWords,
} from '@flowx/designer/modules/ai-builder/utils/harness-error.util'

type HarnessConversationFilesState = {
  /** The conversation's files as the document center last listed them. */
  makerFiles: readonly MakerFile[]
  /** Where each sent file the maker asked to take out has got to, by its panel row id. */
  fileRemovals: Readonly<Record<string, ConversationFileRemovalState>>
  filePanelOpen: boolean
}

const initialConversationFilesState: HarnessConversationFilesState = {
  makerFiles: [],
  fileRemovals: {},
  filePanelOpen: false,
}

/** The conversation's files in the document center: the panel's list, and a file taken out of
 * the conversation or read again. Which conversation, and the workspace its files live in, are
 * handed in by the session; this leaf owns the list, its order and the removals. */
export const HarnessConversationFilesStore = signalStore(
  { providedIn: 'root' },
  withState(initialConversationFilesState),
  withComputed(({ makerFiles, fileRemovals }) => ({
    /** The conversation's files, one row each, for the file panel. */
    conversationFiles: computed(() => conversationFiles(makerFiles(), fileRemovals())),
    /** The stored entries the maker took out of the conversation. */
    removedFileIds: computed(
      (): ReadonlySet<string> =>
        new Set(
          Object.entries(fileRemovals())
            .filter(([, removal]) => removal.status === ConversationFileRemoval.Removed)
            .map(([id]) => id)
        )
    ),
  })),
  withComputed(({ conversationFiles, removedFileIds }) => ({
    /** Where the document center has each sent file, as its chip in the thread draws it. */
    sentFileStanding: computed((): SentFileStanding => ({
      removedIds: removedFileIds(),
      listedMarks: new Map(conversationFiles().map((row) => [row.id, row.mark])),
    })),
  })),
  withMethods((store, api = inject(MakerFilesApi)) => {
    // Bumped by every list and every reset: only the newest list for the conversation on screen
    // lands, as a slow older answer would put back a state the newer one has moved past.
    let listRequest = 0
    // Bumped by every reset: an answer about a conversation the maker has left lands nowhere.
    let conversation = 0
    // The stored files whose Retry is on its way: a second click would ask app-manager again for
    // an entry the first one is already replacing.
    const retrying = new Set<string>()
    // The next list while a file is being read: app-manager reads a retried file in the background
    // and nothing streams its outcome.
    let nextList: ReturnType<typeof setTimeout> | undefined

    const stopListing = (): void => clearTimeout(nextList)

    const call = <T>(request: Observable<T>): Promise<T> =>
      firstValueFrom(request).catch((error: unknown) => {
        throw inServiceWords(error)
      })

    /** Every page of one conversation's files, until app-manager's total or an empty page. */
    const listAll = async (
      sessionId: string,
      workspaceId: string | undefined,
      page = 0,
      listed: readonly MakerFile[] = []
    ): Promise<readonly MakerFile[]> => {
      const { content, total } = await call(api.list(sessionId, workspaceId, page))
      const all = [...listed, ...content]

      return content.length > 0 && all.length < total
        ? listAll(sessionId, workspaceId, page + 1, all)
        : all
    }

    const setRemoval = (id: string, removal: ConversationFileRemovalState): void =>
      patchState(store, (state) => ({ fileRemovals: { ...state.fileRemovals, [id]: removal } }))

    /** The conversation's files from the document center, fresh. A list that cannot be fetched
     * keeps the last one; the panel says nothing new. The open panel lists them again while one
     * is being read. */
    const load = async (
      sessionId: string | null,
      workspaceId: string | undefined
    ): Promise<void> => {
      if (!sessionId) {
        return
      }

      const request = ++listRequest

      stopListing()

      try {
        const listed = await listAll(sessionId, workspaceId)

        if (request === listRequest) {
          patchState(store, { makerFiles: listed })
        }
      } catch {
        // The last list stands.
      }

      const isBeingRead = store
        .makerFiles()
        .some((file) => file.state === MakerFileState.Processing)

      if (request === listRequest && store.filePanelOpen() && isBeingRead) {
        nextList = setTimeout(() => void load(sessionId, workspaceId), FILE_LIST_POLL_MS)
      }
    }

    return {
      load,

      /** Open the file panel, listing the files afresh. */
      open: (sessionId: string | null, workspaceId: string | undefined): void => {
        patchState(store, { filePanelOpen: true })
        void load(sessionId, workspaceId)
      },

      close: (): void => {
        stopListing()
        patchState(store, { filePanelOpen: false })
      },

      /** Take a file out of the conversation, by its stored entry. */
      remove: async (id: string, workspaceId: string | undefined): Promise<void> => {
        const removing = conversation

        setRemoval(id, { status: ConversationFileRemoval.Removing, error: null })

        try {
          await call(api.remove(id, workspaceId))

          if (removing === conversation) {
            setRemoval(id, { status: ConversationFileRemoval.Removed, error: null })
          }
        } catch (error) {
          if (removing === conversation) {
            setRemoval(id, {
              status: ConversationFileRemoval.Failed,
              error: removeRefusalMessage(error),
            })
          }
        }
      },

      /** Index a file that failed processing again, by its stored entry: the entry it now lives
       * under, or null while a retry of it is already on its way. */
      retry: async (id: string, workspaceId: string | undefined): Promise<string | null> => {
        if (retrying.has(id)) {
          return null
        }

        const retryingIn = conversation

        retrying.add(id)

        try {
          return (await call(api.retry(id, workspaceId))).storeEntryUuid
        } finally {
          // Since a reset, the guard is a later retry's.
          if (retryingIn === conversation) {
            retrying.delete(id)
          }
        }
      },

      /** The conversation was left: its list, removals and panel go with it. */
      reset: (): void => {
        listRequest += 1
        conversation += 1
        stopListing()
        retrying.clear()
        patchState(store, initialConversationFilesState)
      },
    }
  })
)

/** How often the open file panel lists the files again while one is being read. */
export const FILE_LIST_POLL_MS = 3000

/** A refused remove in words: the service's own, or ours when it still carries only its HTTP
 * status. */
function removeRefusalMessage(error: unknown): string {
  const status = harnessErrorHttpStatus(error)

  if (status === undefined) {
    return harnessErrorMessage(error)
  }

  return status === HttpStatusCode.NotFound
    ? FILE_PANEL_TEXTS.notFound
    : FILE_PANEL_TEXTS.removeFailed
}
